export interface SessionState {
  authenticated: boolean;
  user?: { email: string | null; verificationLevel: string };
}

export async function getSession(): Promise<SessionState> {
  try {
    const res = await fetch('/auth/me', { credentials: 'same-origin' });
    if (!res.ok) return { authenticated: false };
    return (await res.json()) as SessionState;
  } catch {
    return { authenticated: false };
  }
}

export async function logout(): Promise<void> {
  await fetch('/auth/logout', { method: 'POST', credentials: 'same-origin' });
}

/** Redirect the browser into the HeivolID login ceremony. */
export function login(): void {
  window.location.href = '/auth/login';
}

/**
 * Silent re-auth loop guard. When the session cookie has expired but the user is
 * still signed in to HeivolID (24h JWT on the user-site), bouncing through
 * /auth/login is seamless — so we auto-redirect ONCE instead of dropping the user
 * on the login screen. sessionStorage (per-tab) flags the attempt so a failed
 * bounce falls back to the manual login screen instead of looping.
 */
const REAUTH_FLAG = 'hv_finance_reauth';

export function canAttemptSilentReauth(): boolean {
  // Never auto-retry when the OIDC callback itself just failed.
  if (new URLSearchParams(window.location.search).has('login_error')) return false;
  try {
    return sessionStorage.getItem(REAUTH_FLAG) === null;
  } catch {
    return false; // storage unavailable → no loop guard possible → don't auto-redirect
  }
}

export function beginSilentReauth(): void {
  try {
    sessionStorage.setItem(REAUTH_FLAG, '1');
  } catch {
    return; // can't arm the guard → bail rather than risk a redirect loop
  }
  login();
}

export function clearSilentReauthFlag(): void {
  try {
    sessionStorage.removeItem(REAUTH_FLAG);
  } catch {
    /* ignore */
  }
}
